// Email input handling, ported from the design's STEP 3 (its own domain list and
// edit-distance check; no package). maskEmail and EMAIL_RE live together on
// purpose: one decides what may be typed, the other what counts as valid.

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmail(value) {
  return EMAIL_RE.test(String(value || '').trim());
}

// Sanitises as the customer types: lower case, no spaces, only legal
// characters. !#$%&'*+/=?^_`{|}~ are legal before the @ but never in a domain.
export function maskEmail(raw) {
  let v = (raw || '').toLowerCase().replace(/\s+/g, '');
  v = v.replace(/[^a-z0-9!#$%&'*+/=?^_`{|}~.@-]/g, '');
  const at = v.indexOf('@');
  if (at !== -1) {
    const local = v.slice(0, at).replace(/@/g, '');
    const domain = v.slice(at + 1).replace(/@/g, '').replace(/[!#$%&'*+/=?^_`{|}~]/g, '');
    v = `${local}@${domain}`;
  }
  return v.replace(/\.{2,}/g, '.').replace(/^\.+/, '').slice(0, 254);
}

// A dangling '.', '@' or '-' is an unfinished address, not a mistake.
export function trimEmailTail(value) {
  return maskEmail(value).replace(/[.@-]+$/, '');
}

// US and Canada only, ordered by share: completion offers the first prefix
// match ("g" -> gmail.com). Webmail is the same either side of the border; the
// ISP tail is not ("ro" is Rogers in Canada, Roadrunner in the US).
const WEBMAIL = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com',
  'me.com', 'live.com', 'msn.com', 'googlemail.com', 'mac.com', 'protonmail.com', 'proton.me'];
const ISP_US = ['comcast.net', 'verizon.net', 'att.net', 'sbcglobal.net', 'cox.net', 'charter.net',
  'bellsouth.net', 'earthlink.net', 'optonline.net', 'frontier.com', 'roadrunner.com'];
const ISP_CA = ['rogers.com', 'shaw.ca', 'telus.net', 'bell.net', 'sympatico.ca', 'videotron.ca',
  'cogeco.ca', 'eastlink.ca', 'hotmail.ca', 'yahoo.ca', 'outlook.ca', 'live.ca'];
const ISP_TAIL = ['rocketmail.com', 'ymail.com'];

// Ordered by the delivery country: the other market's domains stay in the list
// (a Canadian buyer shipping to a US address may still be on Rogers) but rank
// below the local ones.
function domainsFor(country) {
  const local = country === 'CA' ? ISP_CA : ISP_US;
  const other = country === 'CA' ? ISP_US : ISP_CA;
  return WEBMAIL.concat(local, other, ISP_TAIL);
}

// Completion, not correction: the domain is still being typed, so offer to
// finish it. Silent once a dot is present; a complete-looking domain is the
// blur-time misspelling check's job, and two suggestions at once is noise.
export function emailSuggestion(raw, country) {
  const v = String(raw || '').trim().toLowerCase();
  const at = v.lastIndexOf('@');
  if (at < 1) return '';
  const local = v.slice(0, at);
  const dom = v.slice(at + 1);
  if (!dom || dom.indexOf('.') !== -1) return '';
  const hit = domainsFor(country).find((d) => d.indexOf(dom) === 0 && d !== dom);
  return hit ? `${local}@${hit}` : '';
}

function distance(a, b) {
  const m = a.length;
  const n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i += 1) {
    const cur = [i];
    for (let j = 1; j <= n; j += 1) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

// Deliberately an explicit table, not edit distance: ".co" and ".om" are real
// TLDs one edit from ".com", and silently rewriting a valid address is worse
// than leaving a typo for the customer to spot.
const TLD_FIX = {
  con: 'com', cpm: 'com', cim: 'com', ocm: 'com', cmo: 'com', comm: 'com',
  coom: 'com', ccom: 'com', xom: 'com', vom: 'com', dom: 'com', clm: 'com', cxom: 'com',
  nte: 'net', ner: 'net', nett: 'net', ent: 'net', bet: 'net',
  ogr: 'org', orgg: 'org', ord: 'org',
  cca: 'ca', caa: 'ca', va: 'ca', xa: 'ca',
};

// gamil.com -> gmail.com. Returns '' when there is nothing to correct.
export function correctEmail(value, country) {
  const v = String(value || '').trim();
  const at = v.lastIndexOf('@');
  if (at < 1) return '';
  const domain = v.slice(at + 1);
  const known = domainsFor(country);
  if (!domain || known.indexOf(domain) !== -1) return '';
  // One edit for short domains, two for long ones (gamil -> gmail is a swap, so 2).
  let best = '';
  let bestD = 3;
  known.forEach((d) => {
    const limit = d.length >= 8 ? 2 : 1;
    const dist = distance(domain, d);
    if (dist <= limit && dist < bestD) { best = d; bestD = dist; }
  });
  if (best) return v.slice(0, at + 1) + best;
  // The domain isn't one we know, but the TLD is still almost certainly a slip.
  const dot = domain.lastIndexOf('.');
  if (dot > 0) {
    const fix = TLD_FIX[domain.slice(dot + 1)];
    if (fix) return v.slice(0, at + 1) + domain.slice(0, dot + 1) + fix;
  }
  return '';
}
